import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useApp } from '@/contexts/AppContext';
import { ShieldCheck, Lock, Database, FileCheck, KeyRound, Globe2 } from 'lucide-react';

const SECTIONS = [
  {
    icon: Lock,
    title: 'Data residency',
    body: 'Customer evidence stays in-region. US-East-1 (Virginia) primary; EU-Central-1 (Frankfurt) optional. No cross-region replication of raw evidence without explicit tenant opt-in.',
  },
  {
    icon: Database,
    title: 'Encryption',
    body: 'AES-256 at rest via AWS KMS customer-managed keys. TLS 1.3 in transit. Per-tenant key segregation, automatic 90-day key rotation, and BYOK supported on Enterprise tier.',
  },
  {
    icon: FileCheck,
    title: 'Retention & immutability',
    body: 'Evidence retained 14 months by default (configurable up to 7 years). S3 Object Lock — Compliance Mode prevents tampering, even by APEX operators. Hashes notarised hourly.',
  },
  {
    icon: KeyRound,
    title: 'Access controls',
    body: 'SSO via SAML 2.0 / OIDC. MFA enforced for all human access. Operator access is just-in-time, time-bound, and recorded in a separate immutable audit trail customers can subscribe to.',
  },
  {
    icon: Globe2,
    title: 'Compliance posture',
    body: 'SOC 2 Type II audited. ISO 27001 certified. HIPAA-eligible workloads supported. PCI DSS aligned for in-scope evidence handling. DPA + SCCs available on request.',
  },
];

export default function DataSecurityModal() {
  const { dataSecurityOpen, setDataSecurityOpen } = useApp();
  return (
    <Dialog open={dataSecurityOpen} onOpenChange={setDataSecurityOpen}>
      <DialogContent data-testid="data-security-modal" className="sm:max-w-[640px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold tracking-tight text-apex-ink flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-apex-lens" />
            Data &amp; Security
          </DialogTitle>
          <DialogDescription className="text-sm text-apex-neutral">
            How APEX handles your evidence — residency, encryption, retention, access, and compliance.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {SECTIONS.map(({ icon: Icon, title, body }) => (
            <div key={title} data-testid={`data-security-section-${title.toLowerCase().replace(/[^a-z]+/g, '-')}`} className="flex gap-3 p-3 rounded-lg border border-slate-200 bg-white">
              <div className="shrink-0 w-9 h-9 rounded-md bg-apex-lens/10 text-apex-lens flex items-center justify-center">
                <Icon className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <h4 className="text-sm font-semibold text-apex-ink">{title}</h4>
                <p className="text-xs text-apex-ink-2 leading-relaxed mt-0.5">{body}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 rounded-lg bg-slate-50 border border-slate-200 p-3 text-[11px] text-apex-neutral leading-relaxed font-mono">
          Data Processing Agreement, SOC 2 report, ISO 27001 certificate, and pen-test letters available under NDA. Contact <span className="text-apex-lens">trust@apex.example</span>.
        </div>
      </DialogContent>
    </Dialog>
  );
}
